import { call } from '@advanza/api'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { MappingItem, OptionItem } from './types'

interface LocalizedServiceMappingState {
    mappings: MappingItem[]
    canonicalOptions: OptionItem[]
    canonicalServiceName: string | null
    isLoading: boolean
    isSaving: boolean
    error: string | null
}

const initialState: LocalizedServiceMappingState = {
    mappings: [],
    canonicalOptions: [],
    canonicalServiceName: null,
    isLoading: false,
    isSaving: false,
    error: null,
}

export const fetchMappings = createAsyncThunk(
    'languageServiceMappings/fetchMappings',
    async (serviceId: number, { rejectWithValue }) => {
        try {
            const response: { mappings: MappingItem[] } = await call(
                `office/services/get-localized-option-mappings/${serviceId}`,
            )
            return response.mappings ?? []
        } catch (error) {
            return rejectWithValue(
                error instanceof Error ? error.message : 'Failed to fetch mappings',
            )
        }
    },
)

export const fetchCanonicalServiceOptions = createAsyncThunk(
    'languageServiceMappings/fetchCanonicalOptions',
    async (canonicalServiceId: number, { rejectWithValue }) => {
        try {
            const response: {
                item?: { name?: string; questions?: Array<{ options?: OptionItem[] }> }
            } = await call(`office/services/get-one/${canonicalServiceId}`)

            const opts: OptionItem[] = []
            response.item?.questions?.forEach((question) => {
                question.options?.forEach((option) => opts.push(option))
            })
            return { options: opts, name: response.item?.name ?? null }
        } catch (error) {
            return rejectWithValue(
                error instanceof Error ? error.message : 'Failed to fetch canonical options',
            )
        }
    },
)

export const saveMappings = createAsyncThunk(
    'languageServiceMappings/saveMappings',
    async (
        { serviceId, mappings }: { serviceId: number; mappings: MappingItem[] },
        { rejectWithValue, dispatch },
    ) => {
        try {
            await call(`office/services/save-localized-option-mappings/${serviceId}`, {
                json: {
                    mappings: mappings.map(({ canonical_option_id, localized_option_id }) => ({
                        canonical_option_id,
                        localized_option_id,
                    })),
                },
            })
            dispatch(fetchMappings(serviceId))
        } catch (error) {
            return rejectWithValue(
                error instanceof Error ? error.message : 'Failed to save mappings',
            )
        }
    },
)

const localizedServiceMappingSlice = createSlice({
    name: 'languageServiceMappings',
    initialState,
    reducers: {
        addMapping: (state, action: { payload: MappingItem }) => {
            const exists = state.mappings.some(
                (m) => m.canonical_option_id === action.payload.canonical_option_id,
            )
            if (!exists) {
                state.mappings.push(action.payload)
            }
        },
        removeMapping: (state, action: { payload: number }) => {
            state.mappings = state.mappings.filter((m) => m.canonical_option_id !== action.payload)
        },
        resetState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchMappings.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(fetchMappings.fulfilled, (state, action) => {
                state.isLoading = false
                state.mappings = action.payload
            })
            .addCase(fetchMappings.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
            })
            .addCase(fetchCanonicalServiceOptions.fulfilled, (state, action) => {
                state.canonicalOptions = action.payload.options
                state.canonicalServiceName = action.payload.name
            })
            .addCase(fetchCanonicalServiceOptions.rejected, (state) => {
                state.canonicalOptions = []
                state.canonicalServiceName = null
            })
            .addCase(saveMappings.pending, (state) => {
                state.isSaving = true
            })
            .addCase(saveMappings.fulfilled, (state) => {
                state.isSaving = false
            })
            .addCase(saveMappings.rejected, (state, action) => {
                state.isSaving = false
                state.error = action.payload as string
            })
    },
})

export const { addMapping, removeMapping, resetState } = localizedServiceMappingSlice.actions
export default localizedServiceMappingSlice.reducer
